/**
 * AWS Usage (ADJUSTMENT) scopes:
 * - Eligible: `SERVICE` scopes + optional `REGION` (include/exclude from UI).
 * - Ineligible: `INELIGIBLE_SERVICE` scopes (values from metadata `ineligible_service_codes`) + optional `REGION`.
 * Ineligible service scopes always send `operator: INCLUDE`.
 */

/** Payload `scope_type` for AWS Usage ineligible service codes (eligible uses SERVICE). */
export const BILLING_SCOPE_TYPE_INELIGIBLE_SERVICE = "INELIGIBLE_SERVICE";

/** UI / create payload helper: eligible adjustment row in a rule block. */
export const ADJUSTMENT_PARTITION_ELIGIBLE = "ELIGIBLE";

/** UI / create payload helper: ineligible adjustment row (service codes from ineligible_service_codes). */
export const ADJUSTMENT_PARTITION_INELIGIBLE = "INELIGIBLE";

const LEGACY_INELIGIBLE_ADJUSTMENT_TYPES = new Set(["INELIGIBLE_MARGIN", "INELIGIBLE_DISCOUNT"]);

export function isDeprecatedAdjustmentRuleType(type) {
  return LEGACY_INELIGIBLE_ADJUSTMENT_TYPES.has(type);
}

/** Map legacy API-only types to MARGIN/DISCOUNT + partition. */
export function mapLegacyAdjustmentTypeToTypeAndPartition(ruleType) {
  if (ruleType === "INELIGIBLE_MARGIN") {
    return { type: "MARGIN", partition: ADJUSTMENT_PARTITION_INELIGIBLE };
  }
  if (ruleType === "INELIGIBLE_DISCOUNT") {
    return { type: "DISCOUNT", partition: ADJUSTMENT_PARTITION_INELIGIBLE };
  }
  return { type: ruleType, partition: ADJUSTMENT_PARTITION_ELIGIBLE };
}

/**
 * Create/update API `rule_type`: UI keeps MARGIN/DISCOUNT + adjustmentPartition;
 * ineligible partition must send INELIGIBLE_MARGIN / INELIGIBLE_DISCOUNT.
 * @param {string} uiType - MARGIN, DISCOUNT, or legacy INELIGIBLE_* (normalized internally)
 * @param {string} [adjustmentPartition]
 */
export function mapAdjustmentTypeAndPartitionToApiRuleType(uiType, adjustmentPartition) {
  const { type: base } = mapLegacyAdjustmentTypeToTypeAndPartition(uiType);
  if (adjustmentPartition === ADJUSTMENT_PARTITION_INELIGIBLE) {
    if (base === "DISCOUNT") return "INELIGIBLE_DISCOUNT";
    if (base === "MARGIN") return "INELIGIBLE_MARGIN";
  }
  return base;
}

/** Normalize selectedRules when loading persisted state or legacy data. */
export function migrateSelectedRulesAdjustmentPartitions(selectedRules) {
  return (selectedRules || []).map((r) => {
    if (r.category !== "ADJUSTMENT") return r;
    if (LEGACY_INELIGIBLE_ADJUSTMENT_TYPES.has(r.type)) {
      const { type, partition } = mapLegacyAdjustmentTypeToTypeAndPartition(r.type);
      return { ...r, type, adjustmentPartition: partition };
    }
    if (r.adjustmentPartition) return r;
    return { ...r, adjustmentPartition: ADJUSTMENT_PARTITION_ELIGIBLE };
  });
}

/** Normalize metadata list responses (service_codes, ineligible_service_codes, etc.). */
export function normalizeMetadataCodeList(responseData) {
  if (!responseData || typeof responseData !== "object") return [];
  const list =
    responseData.values ??
    responseData.service_codes ??
    responseData.ineligible_service_codes ??
    [];
  return Array.isArray(list) ? list : [];
}

/**
 * @param {unknown} serviceCodes - full catalog from `service_codes` metadata
 * @param {Set<string>} ineligibleSet - codes from `ineligible_service_codes` metadata
 */
export function getEligibleServiceCodesFromApi(serviceCodes, ineligibleSet) {
  const set = ineligibleSet instanceof Set ? ineligibleSet : new Set();
  if (!Array.isArray(serviceCodes)) return [];
  return serviceCodes.filter((s) => !set.has(s));
}

/**
 * @param {unknown} selectedServices
 * @param {Set<string>} ineligibleSet
 */
export function partitionSelectedServicesForAwsUsage(selectedServices, ineligibleSet) {
  const set = ineligibleSet instanceof Set ? ineligibleSet : new Set();
  if (!Array.isArray(selectedServices)) return { eligible: [], ineligible: [] };
  const eligible = [];
  const ineligible = [];
  for (const s of selectedServices) {
    if (set.has(s)) ineligible.push(s);
    else eligible.push(s);
  }
  return { eligible, ineligible };
}
